<template>
  <div class="login-wrapper surface-ground min-h-screen flex align-items-center justify-content-center p-3 md:p-6">

    <!-- Tarjeta adaptable -->
    <div class="surface-card shadow-4 border-round-xl w-full login-card overflow-hidden">

      <!-- Contenido Principal -->
      <div class="px-4 py-5 md:px-6">
        <!-- Logos Superiores -->
        <div class="text-center mb-3">
          <img src="images/blocks/logos/logoasociadozapi.png" alt="Logo" class="header-logo" />
          <h1 class="text-900 text-2xl md:text-3xl font-bold mt-2 mb-1 tracking-wide">¡BIENVENIDOS!</h1>
          <p class="text-600 text-sm md:text-base m-0">
            Ingrese sus credenciales para acceder al sistema
          </p>
        </div>

        <!-- ======================================================= -->
        <!-- PASO 1: Formulario Normal                               -->
        <!-- ======================================================= -->
        <div class="form-container mx-auto" v-if="!requires2fa">
          <Form @submit="onSubmit" :loading="cargando" :validation-schema="schema" v-slot="{ errors, isSubmitting }">
            <!-- Campo Email -->
            <div class="mb-3 text-left">
              <label class="block text-900 font-bold text-xs md:text-sm mb-1 tracking-wider">
                CORREO ELECTRÓNICO:
              </label>
              <div class="custom-input-box">
                <i class="pi pi-envelope field-icon"></i>
                <Field name="email" type="email" class="custom-field" placeholder="Su Correo Electrónico"
                  autocomplete="email" :class="{ 'field-error': errors.email }" />
              </div>
              <div v-if="errors.email" class="text-red-500 text-xs mt-1">{{ errors.email }}</div>
            </div>

            <!-- Campo Contraseña -->
            <div class="mb-4 text-left">
              <label class="block text-900 font-bold text-xs md:text-sm mb-1 tracking-wider">
                CONTRASEÑA:
              </label>
              <div class="custom-input-box">
                <i class="pi pi-lock field-icon"></i>
                <Field name="password" :type="showPassword ? 'text' : 'password'"
                  class="custom-field custom-field-password" placeholder="Su contraseña" autocomplete="current-password"
                  :class="{ 'field-error': errors.password }" />
                <i :class="['pi toggle-password-icon cursor-pointer', showPassword ? 'pi-eye-slash' : 'pi-eye']"
                  @click="showPassword = !showPassword"></i>
              </div>
              <div v-if="errors.password" class="text-red-500 text-xs mt-1">{{ errors.password }}</div>
            </div>

            <!-- Alerta de Error -->
            <div v-if="errorLogin"
              class="p-message p-message-error p-3 border-round mb-3 text-sm flex align-items-center">
              <i class="pi pi-exclamation-circle mr-2"></i>{{ errorLogin }}
            </div>

            <!-- Botón Login -->
            <div class="mb-2">
              <button type="submit"
                class="custom-btn-submit w-full flex align-items-center justify-content-center cursor-pointer font-bold tracking-wide"
                :disabled="isSubmitting || cargando">
                <i class="pi pi-sign-in mr-2"></i>
                <span>INICIAR SESIÓN</span>
              </button>
            </div>

            <div v-if="cargando" class="flex align-items-center justify-content-center mt-3 text-600 text-sm">
              <span>Conectando, por favor espera...</span>
              <i class="pi pi-spin pi-spinner ml-2"></i>
            </div>
          </Form>
        </div>

        <!-- ======================================================= -->
        <!-- PASO 2: 2FA                                            -->
        <!-- ======================================================= -->
        <div class="form-container mx-auto" v-else>
          <div class="text-center mb-4">
            <i class="pi pi-shield text-5xl text-primary mb-2" style="display:block"></i>
            <h3 class="text-800 font-bold mb-1">Verificación en dos pasos</h3>
            <p class="text-600 text-sm">Abre <strong>Google Authenticator</strong> e ingresa el código de 6 dígitos.</p>
          </div>

          <div class="mb-4">
            <InputText v-model="codigoOTP" placeholder="· · · · · ·" class="w-full text-center text-3xl font-bold py-3"
              maxlength="6" style="letter-spacing: 0.5rem" @keyup.enter="onSubmit2FA" autofocus />
          </div>

          <div v-if="error2fa" class="p-message p-message-error p-3 border-round mb-3 text-sm">
            <i class="pi pi-exclamation-circle mr-2"></i>{{ error2fa }}
          </div>

          <Button label="VERIFICAR CÓDIGO" icon="pi pi-check" class="w-full p-3 font-bold p-button-success mb-2"
            :disabled="codigoOTP.length !== 6 || cargando2fa" :loading="cargando2fa" @click="onSubmit2FA" />

          <Button label="Volver al login" icon="pi pi-arrow-left"
            class="w-full p-button-text p-button-secondary text-sm" @click="volverAlLogin" />
        </div>
      </div>

      <!-- ======================================================= -->
      <!-- FOOTER: SOPORTE Y CONTACTO                             -->
      <!-- ======================================================= -->
      <div class="login-footer-bg">
        <!-- Curva decorativa superior -->
        <svg class="footer-wave" viewBox="0 0 1440 120" preserveAspectRatio="none">
          <path
            d="M0,32L80,48C160,64,320,96,480,90.7C640,85,800,43,960,32C1120,21,1280,43,1360,53.3L1440,64L1440,120L0,120Z"
            fill="#e8f4fd"></path>
        </svg>

        <div class="footer-info">
          <div class="footer-inner-container">
            <div class="contact-responsive-box">

              <!-- Ítem 1: Soporte Técnico -->
              <div class="contact-item">
                <div class="contact-icon-bubble mr-2 flex align-items-center justify-content-center flex-shrink-0">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2"
                    stroke-linecap="round" stroke-linejoin="round">
                    <path d="M3 18v-6a9 9 0 0 1 18 0v6"></path>
                    <path
                      d="M21 19a2 2 0 0 1-2 2h-1a2 2 0 0 1-2-2v-3a2 2 0 0 1 2-2h3zM3 19a2 2 0 0 0 2 2h1a2 2 0 0 0 2-2v-3a2 2 0 0 0-2-2H3z">
                    </path>
                  </svg>
                </div>
                <div class="text-left flex flex-column justify-content-center contact-text-block">
                  <span class="contact-main-text">SOPORTE TÉCNICO</span>
                  <span class="contact-sub-text">Estamos para ayudarte</span>
                </div>
              </div>

              <!-- Separador vertical (solo desktop) -->
              <div class="desktop-divider"></div>

              <!-- Ítem 2: Teléfono / WhatsApp -->
              <div class="contact-item">
                <div class="contact-icon-bubble mr-2 flex align-items-center justify-content-center flex-shrink-0">
                  <i class="pi pi-phone"></i>
                </div>
                <div class="text-left flex flex-column justify-content-center contact-text-block">
                  <span class="contact-sub-text">Teléfono / WhatsApp</span>
                  <a href="https://wa.me/59167707838" target="_blank" class="footer-link contact-main-text text-800">
                    6770 7838
                  </a>
                </div>
              </div>

              <!-- Separador vertical (solo desktop) -->
              <div class="desktop-divider"></div>

              <!-- Ítem 3: Correo Electrónico -->
              <div class="contact-item">
                <div class="contact-icon-bubble mr-2 flex align-items-center justify-content-center flex-shrink-0">
                  <i class="pi pi-envelope"></i>
                </div>
                <div class="text-left flex flex-column justify-content-center contact-text-block">
                  <span class="contact-sub-text">Correo Electrónico</span>
                  <a href="mailto:soporte@escalanet.com.bo" class="footer-link contact-main-text text-primary">
                    soporte@escalanet.com.bo
                  </a>
                </div>
              </div>

              <!-- Separador vertical (solo desktop) -->
              <div class="desktop-divider"></div>

              <!-- Ítem 4: Sitio Web -->
              <div class="contact-item">
                <div class="contact-icon-bubble mr-2 flex align-items-center justify-content-center flex-shrink-0">
                  <i class="pi pi-globe"></i>
                </div>
                <div class="text-left flex flex-column justify-content-center contact-text-block">
                  <span class="contact-sub-text">Sitio Web</span>
                  <a href="https://escalanet.com.bo/" target="_blank"
                    class="footer-link contact-main-text text-primary">
                    www.escalanet.com.bo
                  </a>
                </div>
              </div>

            </div>
          </div>
        </div>

        <!-- Barra inferior azul oscura -->
        <div class="footer-bar text-center py-2 px-3 text-white text-xs md:text-sm">
          DESARROLLADO POR: <strong>ESCALANET</strong> &nbsp;|&nbsp; SOLUCIONES QUE CONECTAN TU NEGOCIO
        </div>
      </div>

    </div>
  </div>
</template>

<script setup>
import { ref, onBeforeMount, onMounted, watch } from "vue";
import { useRouter } from "vue-router";
import { Form, Field } from "vee-validate";
import * as Yup from "yup";
import { useAuth, useConexion } from "@/stores";
import { aplicarVersionNueva } from "@/registrarServiceWorker";

const router = useRouter();
const authStore = useAuth();

const cargando = ref(false);
const errorLogin = ref("");

// Variable reactiva para la visibilidad de la contraseña
const showPassword = ref(false);

const requires2fa = ref(false);
const pending2faUserId = ref(null);
const codigoOTP = ref("");
const error2fa = ref("");
const cargando2fa = ref(false);

const schema = Yup.object().shape({
  email: Yup.string().required("Ingrese Correo").email("¡Correo inválido!"),
  password: Yup.string()
    .min(8, "La contraseña debe contener más de 8 caracteres!")
    .required("Ingrese Contraseña!"),
});

async function onSubmit(values) {
  cargando.value = true;
  errorLogin.value = "";

  const result = await authStore.userLogin(values);

  if (!result) {
    cargando.value = false;
    return;
  }

  if (result.requires_2fa) {
    pending2faUserId.value = result.user_id;
    requires2fa.value = true;
    cargando.value = false;
    return;
  }

  if (!result.success) {
    errorLogin.value = result.mensaje || "Error al iniciar sesión.";
  }

  cargando.value = false;
}

async function onSubmit2FA() {
  if (codigoOTP.value.length !== 6) return;

  cargando2fa.value = true;
  error2fa.value = "";

  const result = await authStore.verifyLogin2FA({
    user_id: pending2faUserId.value,
    otp: codigoOTP.value,
  });

  if (!result.success) {
    error2fa.value = result.mensaje || "Código incorrecto. Intente nuevamente.";
    codigoOTP.value = "";
  }

  cargando2fa.value = false;
}

function volverAlLogin() {
  requires2fa.value = false;
  pending2faUserId.value = null;
  codigoOTP.value = "";
  error2fa.value = "";
  errorLogin.value = "";
}

onBeforeMount(() => {
  if (localStorage.getItem("token")) router.push("/");
});

// Etapa 6 (D3): en el login no hay carrito que perder: una versión nueva esperando se aplica sola
// (al montar, o si termina de descargarse mientras se está en esta pantalla)
onMounted(() => {
  aplicarVersionNueva();
});
watch(() => useConexion().versionNueva, (nueva) => {
  if (nueva) aplicarVersionNueva();
});
</script>

<style scoped>
/* Contenedor principal */
.login-card {
  max-width: 860px;
  background-color: #ffffff;
  border-radius: 12px;
}

.form-container {
  max-width: 480px;
  width: 100%;
}

.header-logo {
  max-height: 125px;
  width: auto;
  max-width: 100%;
  object-fit: contain;
}

/* Encapsulado de inputs */
.custom-input-box {
  position: relative;
  display: block;
  width: 100%;
}

.field-icon {
  position: absolute;
  top: 50%;
  left: 1rem;
  transform: translateY(-50%);
  font-size: 1.15rem;
  color: #64748b;
  pointer-events: none;
  z-index: 5;
}

.custom-field {
  width: 100%;
  padding: 0.9rem 1rem 0.9rem 2.85rem !important;
  font-size: 0.95rem;
  color: #334155;
  background: #ffffff;
  border: 1px solid #cbd5e1;
  border-radius: 6px;
  outline: none;
  box-sizing: border-box;
  display: block;
  transition: border-color 0.2s, box-shadow 0.2s;
}

.custom-field:focus {
  border-color: #0288d1 !important;
  box-shadow: 0 0 0 1px #0288d1 !important;
}

.custom-field.field-error {
  border-color: #ef4444 !important;
}

/* Espacio derecho en el input para que la contraseña no toque el icono */
.custom-field-password {
  padding-right: 2.85rem !important;
}

/* Estilos globales para el icono del ojo */
.toggle-password-icon {
  position: absolute;
  top: 50%;
  right: 1rem;
  transform: translateY(-50%);
  font-size: 1.15rem;
  color: #64748b;
  z-index: 5;
  transition: color 0.2s;
  cursor: pointer;
}

.toggle-password-icon:hover {
  color: #0288d1;
}

/* Botón Iniciar Sesión */
.custom-btn-submit {
  background-color: #0288d1;
  border: none;
  color: #ffffff;
  border-radius: 6px;
  padding: 0.95rem;
  font-size: 1rem;
  transition: background-color 0.2s;
}

.custom-btn-submit:hover {
  background-color: #0277bd;
}

.custom-btn-submit:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

/* Pie con fondo decorativo */
.login-footer-bg {
  position: relative;
  background: #e8f4fd;
}

.footer-wave {
  position: absolute;
  top: -28px;
  left: 0;
  width: 100%;
  height: 30px;
  pointer-events: none;
}

.footer-info {
  position: relative;
  z-index: 1;
}

/* ==================================================== */
/* UNIFICACIÓN GLOBAL DE TAMAÑOS (ESCRITORIO Y MÓVIL)  */
/* ==================================================== */

.contact-main-text {
  font-size: 0.95rem !important;
  font-weight: 700 !important;
  line-height: 1.25 !important;
  white-space: nowrap;
}

.contact-sub-text {
  font-size: 0.80rem !important;
  color: #64748b !important;
  line-height: 1.2 !important;
  white-space: nowrap;
}

.contact-icon-bubble {
  background: #0288d1;
  color: #ffffff;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  font-size: 1.2rem;
}

/* ==================================================== */
/* VISTA COMPUTADORA / LAPTOP (md+)                     */
/* ==================================================== */
@media (min-width: 768px) {
  .footer-inner-container {
    padding: 1rem 1.5rem;
    width: 100%;
  }

  .contact-responsive-box {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    gap: 12px;
  }

  .contact-item {
    display: flex;
    align-items: center;
    padding: 4px 6px;
    flex-shrink: 0;
  }

  .desktop-divider {
    width: 1px;
    height: 36px;
    background: #cbd5e1;
    flex-shrink: 0;
  }
}

/* ==================================================== */
/* VISTA CELULAR / MÓVIL (< 768px)                      */
/* ==================================================== */
@media (max-width: 767px) {
  .footer-inner-container {
    padding: 0.5rem 1rem 1.25rem 1rem;
    width: 100%;
    box-sizing: border-box;
  }

  .contact-responsive-box {
    display: flex;
    flex-direction: column;
    width: 100% !important;
    background: #ffffff;
    border: 1px solid #d0e1fd;
    border-radius: 12px;
    padding: 14px 16px;
    box-shadow: 0 4px 14px rgba(2, 136, 209, 0.08);
    box-sizing: border-box;
    margin: 0;
  }

  .desktop-divider {
    display: none;
  }

  .contact-item {
    display: flex;
    align-items: center;
    padding: 12px 6px;
    border-bottom: 1px dashed #e2e8f0;
    width: 100%;
    box-sizing: border-box;
  }

  .contact-item:last-child {
    border-bottom: none;
    padding-bottom: 4px;
  }
}

.footer-link {
  text-decoration: none;
  display: block;
}

.footer-link:hover {
  text-decoration: underline;
}

.footer-bar {
  background-color: #004b87;
  letter-spacing: 0.5px;
}
</style>